import type WebApiClient from "../../api/WebApiClient.js";
import {
  LOAD_ALL_FEATURE_TYPES_FAILURE,
  LOAD_ALL_FEATURE_TYPES_REQUEST,
  LOAD_ALL_FEATURE_TYPES_SUCCESS,
  LOAD_ALL_SEQUENCE_TYPES_FAILURE,
  LOAD_ALL_SEQUENCE_TYPES_REQUEST,
  LOAD_ALL_SEQUENCE_TYPES_SUCCESS,
  LOAD_ALL_USER_GROUPS_FAILURE,
  LOAD_ALL_USER_GROUPS_REQUEST,
  LOAD_ALL_USER_GROUPS_SUCCESS,
  LOAD_APPROVED_WORKFLOWS_FAILURE,
  LOAD_APPROVED_WORKFLOWS_REQUEST,
  LOAD_APPROVED_WORKFLOWS_SUCCESS,
  LOAD_SAMPLE_TYPES_FAILURE,
  LOAD_SAMPLE_TYPES_REQUEST,
  LOAD_SAMPLE_TYPES_SUCCESS,
} from "../action-types.js";
import type { IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { createErrorHandler } from "@/app-utils/errors.ts";
import type { SampleType, SequenceFeatureTypes, SequenceType, UserGroup, Workflow } from "@/graphql.ts";

/**
 * State definition
 */

export interface IListingState {
  sampleTypes: SampleType[];
  isLoadingSampleTypes: boolean;
  userGroups: UserGroup[];
  isLoadingUserGroups: boolean;
  approvedWorkflows: Workflow[];
  isLoadingApprovedWorkflows: boolean;
  isLoadingSampleStatuses: boolean;
  sequenceFeatureTypes: SequenceFeatureTypes;
  isLoadingSequenceFeatureTypes: boolean;
  sequenceTypes: SequenceType[];
  isLoadingSequenceTypes: boolean;
}

const initialState: IListingState = {
  sampleTypes: [],
  isLoadingSampleTypes: false,
  userGroups: [],
  isLoadingUserGroups: false,
  approvedWorkflows: [],
  isLoadingApprovedWorkflows: false,
  isLoadingSampleStatuses: false,
  sequenceFeatureTypes: null,
  isLoadingSequenceFeatureTypes: false,
  sequenceTypes: null,
  isLoadingSequenceTypes: false,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_SAMPLE_TYPES_REQUEST]: (state: IListingState): IListingState => {
    return { ...state, isLoadingSampleTypes: true };
  },
  [LOAD_SAMPLE_TYPES_SUCCESS]: (state: IListingState, sampleTypes: SampleType[]): IListingState => {
    return { ...state, sampleTypes, isLoadingSampleTypes: false };
  },
  [LOAD_SAMPLE_TYPES_FAILURE]: (state: IListingState): IListingState => {
    return { ...state, isLoadingSampleTypes: false };
  },
  [LOAD_ALL_USER_GROUPS_REQUEST]: (state: IListingState): IListingState => {
    return { ...state, isLoadingUserGroups: true };
  },
  [LOAD_ALL_USER_GROUPS_SUCCESS]: (state: IListingState, userGroups: UserGroup[]): IListingState => {
    return { ...state, userGroups, isLoadingUserGroups: false };
  },
  [LOAD_ALL_USER_GROUPS_FAILURE]: (state: IListingState): IListingState => {
    return { ...state, isLoadingUserGroups: false };
  },

  [LOAD_APPROVED_WORKFLOWS_REQUEST]: (state: IListingState): IListingState => {
    return { ...state, isLoadingApprovedWorkflows: true };
  },
  [LOAD_APPROVED_WORKFLOWS_SUCCESS]: (state: IListingState, approvedWorkflows: Workflow[]): IListingState => {
    return { ...state, approvedWorkflows, isLoadingApprovedWorkflows: false };
  },
  [LOAD_APPROVED_WORKFLOWS_FAILURE]: (state: IListingState): IListingState => {
    return { ...state, isLoadingApprovedWorkflows: false };
  },

  [LOAD_ALL_FEATURE_TYPES_REQUEST]: (state: IListingState): IListingState => {
    return { ...state, isLoadingSequenceFeatureTypes: true };
  },
  [LOAD_ALL_FEATURE_TYPES_SUCCESS]: (state: IListingState, featureTypes: SequenceFeatureTypes): IListingState => {
    return { ...state, sequenceFeatureTypes: featureTypes, isLoadingSequenceFeatureTypes: false };
  },
  [LOAD_ALL_FEATURE_TYPES_FAILURE]: (state: IListingState): IListingState => {
    return { ...state, isLoadingSequenceFeatureTypes: false };
  },

  [LOAD_ALL_SEQUENCE_TYPES_REQUEST]: (state: IListingState): IListingState => {
    return { ...state, isLoadingSequenceTypes: true };
  },
  [LOAD_ALL_SEQUENCE_TYPES_SUCCESS]: (state: IListingState, sequenceTypes: SequenceType[]): IListingState => {
    return { ...state, sequenceTypes, isLoadingSequenceTypes: false };
  },
  [LOAD_ALL_SEQUENCE_TYPES_FAILURE]: (state: IListingState): IListingState => {
    return { ...state, isLoadingSequenceTypes: false };
  },
});

/**
 * Action creators
 */

export function loadSampleTypes() {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<SampleType[]> => {
    dispatch({ type: LOAD_SAMPLE_TYPES_REQUEST });

    return webApiClient.fetchSampleTypes().then(
      (sampleTypes: SampleType[]) => {
        dispatch({
          type: LOAD_SAMPLE_TYPES_SUCCESS,
          payload: sampleTypes,
        });
        return sampleTypes;
      },
      createErrorHandler(dispatch, {
        actionType: LOAD_SAMPLE_TYPES_FAILURE,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

export function loadBasicUserGroups() {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOAD_ALL_USER_GROUPS_REQUEST });

    return webApiClient
      .fetchBasicUserGroups()
      .then((userGroups: UserGroup[]) => {
        dispatch({
          type: LOAD_ALL_USER_GROUPS_SUCCESS,
          payload: userGroups,
        });
        return userGroups;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ALL_USER_GROUPS_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function loadAccessibleUserGroups() {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOAD_ALL_USER_GROUPS_REQUEST });

    return webApiClient
      .fetchAccessibleUserGroups()
      .then((userGroups: UserGroup[]) => {
        dispatch({
          type: LOAD_ALL_USER_GROUPS_SUCCESS,
          payload: userGroups,
        });
        return userGroups;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ALL_USER_GROUPS_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function loadApprovedWorkflows(workflowID: string /* null for all workflows */, playbookTypeId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Workflow[]> => {
    dispatch({ type: LOAD_APPROVED_WORKFLOWS_REQUEST });

    return webApiClient
      .fetchApprovedWorkflows(workflowID, playbookTypeId)
      .then((workflows: Workflow[]) => {
        dispatch({
          type: LOAD_APPROVED_WORKFLOWS_SUCCESS,
          payload: workflows,
        });
        return workflows;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_APPROVED_WORKFLOWS_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function loadSequenceFeatureTypes() {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<SequenceFeatureTypes> => {
    dispatch({ type: LOAD_ALL_FEATURE_TYPES_REQUEST });

    return webApiClient.fetchSequenceFeatureTypes().then(
      (featureTypes: SequenceFeatureTypes) => {
        dispatch({
          type: LOAD_ALL_FEATURE_TYPES_SUCCESS,
          payload: featureTypes,
        });
        return featureTypes;
      },
      createErrorHandler(dispatch, {
        actionType: LOAD_ALL_FEATURE_TYPES_FAILURE,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

export function loadSequenceTypes() {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<SequenceType[]> => {
    dispatch({ type: LOAD_ALL_SEQUENCE_TYPES_REQUEST });

    return webApiClient.fetchSequenceTypes().then(
      (sequenceTypes: SequenceType[]) => {
        dispatch({
          type: LOAD_ALL_SEQUENCE_TYPES_SUCCESS,
          payload: sequenceTypes,
        });
        return sequenceTypes;
      },
      createErrorHandler(dispatch, {
        actionType: LOAD_ALL_SEQUENCE_TYPES_FAILURE,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}
